import React, { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { useAuthStore } from './stores/authStore';
import { useUIStore } from './stores/uiStore';
import { useOrganizationStore } from './stores/organizationStore';
import { Loader } from './components/common';
import { showToast } from './components/common/Toast';
import { useShareTarget } from './hooks';
import { Sidebar } from './components/layout';
import { TaskForm } from './components/tasks';
import { ToastContainer } from './components/common/Toast';
import { AiChatFloat, AiChatPanel } from './components/ai-chat';
import { NotificationStateProvider } from './contexts/NotificationStateContext';
import { RealtimeSubscriptionsProvider } from './contexts/RealtimeSubscriptionsProvider';
import { InboxPage, TodayPage, UpcomingPage, ProjectsPage, LoginPage, UsersPage, ProfilePage, HistoryPage, AttendancePage, SettingsPage, StatisticsPage, RecurringTasksPage, RemindersPage, UserTasksPage, OrganizationsPage, SelectOrganizationPage, KioskPage } from './pages';
import './styles/globals.css';

// FIX: Move ProtectedLayout OUTSIDE of App component
// Defining components inside other components causes unmount/remount on every parent render
// This was causing duplicate API calls and unnecessary re-renders
const ProtectedLayout: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, checkAuth, isLoading: isAuthLoading, user } = useAuthStore();
  const { sidebarOpen, darkMode } = useUIStore();
  const { organizations, fetchOrganizations, isLoading: isOrgLoading, error: orgError, clearError: clearOrgError, activeOrganizationId } = useOrganizationStore();

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  // Fetch organizations when user is authenticated
  useEffect(() => {
    if (isAuthenticated) {
      fetchOrganizations();
    }
  }, [isAuthenticated, fetchOrganizations]);

  // B5: Show error toast if fetching organizations fails before redirect to SelectOrganizationPage
  useEffect(() => {
    if (orgError && isAuthenticated) {
      showToast(orgError, 'error');
      clearOrgError();
    }
  }, [orgError, isAuthenticated, clearOrgError]);

  // Initialize dark mode
  useEffect(() => {
    if (darkMode) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  }, [darkMode]);

  // Show loading spinner only while auth is being checked
  if (isAuthLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
        <Loader size="lg" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // If orgs are still loading, wait briefly then proceed (avoid infinite spinner on API failure)
  if (isOrgLoading && organizations.length === 0) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
        <Loader size="lg" />
      </div>
    );
  }

  // If user is authenticated but has no organizations, show select organization page
  // (this also handles the case where fetchOrganizations failed — user needs to create/join an org)
  if (organizations.length === 0) {
    return <SelectOrganizationPage />;
  }

  // FIX: If the user has organizations but activeOrganizationId is null (or doesn't
  // match any of their orgs), redirect to SelectOrganizationPage so they can pick
  // an org. Without this, the user would reach org-scoped pages (e.g.
  // AttendancePage) which require req.user.activeOrganizationId on the backend,
  // resulting in HTTP 400 errors.
  const userActiveOrgId = user?.activeOrganizationId ?? activeOrganizationId;
  const hasValidActiveOrg = userActiveOrgId && organizations.some(o => o.id === userActiveOrgId);
  if (!hasValidActiveOrg) {
    return <SelectOrganizationPage />;
  }

  return (
    <NotificationStateProvider>
      <RealtimeSubscriptionsProvider>
        <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
        <Sidebar />
        {/* Desktop: margin based on sidebar state. Mobile: no margin */}
        <div className={`transition-all duration-300 ml-0 md:ml-16 ${sidebarOpen ? 'md:ml-64' : ''}`}>
          {children}
        </div>
        <TaskForm />
          <AiChatFloat />
          <AiChatPanel />
      </div>
      </RealtimeSubscriptionsProvider>
    </NotificationStateProvider>
  );
};

const App: React.FC = () => {
  // Handle incoming PWA share-target images and open the new task modal
  useShareTarget();

  // Register the share-target service worker in production
  useEffect(() => {
    if ('serviceWorker' in navigator && import.meta.env.PROD) {
      navigator.serviceWorker
        .register('/sw.js')
        .catch((err) => console.error('Service worker registration failed:', err));
    }
  }, []);

  const isKiosk = window.location.hostname.includes('.checkin.') || 
                  new URLSearchParams(window.location.search).get('kiosk') === 'true';

  if (isKiosk) {
    return (
      <div className="dark">
        <KioskPage />
      </div>
    );
  }

  return (
    <BrowserRouter basename="/app">
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route path="/register" element={<LoginPage />} />
        <Route path="/select-organization" element={<SelectOrganizationPage />} />
        
        <Route path="/" element={<ProtectedLayout>
          <InboxPage />
        </ProtectedLayout>} />
        
        <Route path="/today" element={<ProtectedLayout>
          <TodayPage />
        </ProtectedLayout>} />
        
        <Route path="/upcoming" element={<ProtectedLayout>
          <UpcomingPage />
        </ProtectedLayout>} />
        
        <Route path="/recurring" element={<ProtectedLayout>
          <RecurringTasksPage />
        </ProtectedLayout>} />
        
        <Route path="/projects" element={<ProtectedLayout>
          <ProjectsPage />
        </ProtectedLayout>} />
        
        <Route path="/history" element={<ProtectedLayout>
          <HistoryPage />
        </ProtectedLayout>} />
        
        <Route path="/statistics" element={<ProtectedLayout>
          <StatisticsPage />
        </ProtectedLayout>} />
        
        <Route path="/attendances" element={<ProtectedLayout>
          <AttendancePage />
        </ProtectedLayout>} />
        
        <Route path="/users" element={<ProtectedLayout>
          <UsersPage />
        </ProtectedLayout>} />
        
        <Route path="/profile" element={<ProtectedLayout>
          <ProfilePage />
        </ProtectedLayout>} />
        
        <Route path="/settings" element={<ProtectedLayout>
          <SettingsPage />
        </ProtectedLayout>} />
        
        <Route path="/reminders" element={<ProtectedLayout>
          <RemindersPage />
        </ProtectedLayout>} />

        <Route path="/organizations" element={<ProtectedLayout>
          <OrganizationsPage />
        </ProtectedLayout>} />

        <Route path="/user-tasks" element={<ProtectedLayout>
          <UserTasksPage />
        </ProtectedLayout>} />

        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      <ToastContainer />
    </BrowserRouter>
  );
};

export default App;
